<template>
    <div class="mb-5">
        <h5>{{ $t('Basic Information') }}</h5>

        <div class="mb-3 mt-4">
            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Chinese Business Name') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.name" @keydown="onChange('name')" />
                    <div v-if="error['name']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['name']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('English Business Name') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.enName" @keydown="onChange('enName')" />
                    <div v-if="error['enName']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['enName']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col xl="4" sm="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('Province') }} <span class="text-danger">*</span></label>
                        <Select2 v-model="params.province" :options="provinceOpt" @select="onSelectProvince($event)"
                            :settings='{ escapeMarkup: doEscapeMarkup, placeholder: $t("Province") }'
                            class="form-control" />
                        <div v-if="error['province']" class="invalid-feedback d-block mb-3">
                            {{ $t(error['province']) }}
                        </div>
                    </div>
                </b-col>
                <b-col xl="4" sm="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('City') }} <span class="text-danger">*</span></label>
                        <Select2 v-model="params.city" :options="cityOpt" @select="onSelectCity($event)"
                            :settings='{ escapeMarkup: doEscapeMarkup, placeholder: $t("City") }'
                            class="form-control" />
                        <div v-if="error['city']" class="invalid-feedback d-block mb-3">
                            {{ $t(error['city']) }}
                        </div>
                    </div>
                </b-col>
                <b-col xl="4" sm="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('District') }}</label>
                        <Select2 v-model="params.area" :options="districtOpt" @select="onSelectDistrict($event)"
                            :settings='{ escapeMarkup: doEscapeMarkup, placeholder: $t("District") }'
                            class="form-control" />
                        <div v-if="error['area']" class="invalid-feedback d-block mb-3">
                            {{ $t(error['area']) }}
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-4">
                <b-col xl="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('Address') }} <span class="text-danger">*</span></label>
                        <input type="text" v-model="params.address" class="form-control"
                            @keydown="addressCondition($event)" @keyup="onchangeAddress($event)" />
                        <div v-if="error['address']" class="invalid-feedback d-block mb-3">
                            {{ $t(error['address']) }}
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-4">
                <b-col cols="12" md="3" class="d-flex">
                    <label class="d-flex">{{ $t('Certificate of Registration') }} <span
                            class="text-danger">*</span></label>
                    <span class="position-relative">
                        <button type="button" class="flat-button position-absolute" @click="togglePopup('showPopup_4')">
                            <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                        </button>
                        <NewPopupInfo :name="'certificate_registration'" v-if="showPopup_4"
                            :styleCustom="{ transform: isMobile ? 'translateX(-70%)' : 'translateX(calc(-15vw))' }">
                        </NewPopupInfo>
                    </span>
                </b-col>

                <b-col cols="12" md="9">
                    <FileUploader :type="'browse'" :ref="'orgRegister'" :allowed_types="['jpg', 'jpeg', 'png', 'pdf']"
                        :size="10 * 1024 * 1024" @capture-file="captureFile($event, 'orgRegister')"
                        @remove-image="removeImage($event)">
                    </FileUploader>
                    <div v-if="error['orgRegister']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['orgRegister']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Certificate of Registration Number') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.orgRegisterNo"
                        @keydown="onChange('orgRegisterNo')" />
                    <div v-if="error['orgRegisterNo']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['orgRegisterNo']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-4">
                <b-col cols="12" md="3">
                    <label>{{ $t('Annual Return') }} <span class="text-danger">*</span></label>
                    <span class="position-relative">
                        <button type="button" class="flat-button position-absolute" @click="togglePopup('showPopup_2')">
                            <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                        </button>
                        <NewPopupInfo :name="'annual_return'" v-if="showPopup_2"
                            :styleCustom="{ transform: isMobile ? 'translateX(-50%)' : 'translateX(calc(-10vw))' }">
                            ></NewPopupInfo>
                    </span>
                </b-col>

                <b-col cols="12" md="9">
                    <FileUploader :type="'browse'" :ref="'declarationForm'"
                        :allowed_types="['jpg', 'jpeg', 'png', 'pdf', 'rar', 'zip']" :size="20 * 1024 * 1024"
                        @capture-file="captureFile($event, 'declarationForm')" @remove-image="removeImage($event)">
                    </FileUploader>
                    <div v-if="error['declarationForm']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['declarationForm']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-4">
                <b-col cols="12" md="3">
                    <label>{{ $t('Incorporation Form') }} <span class="text-danger">*</span></label>
                    <span class="position-relative">
                        <button type="button" class="flat-button position-absolute" @click="togglePopup('showPopup_6')">
                            <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                        </button>
                        <NewPopupInfo :name="'form_incorporation'" v-if="showPopup_6"
                            :styleCustom="{ transform: isMobile ? 'translateX(-70%)' : 'translateX(calc(-10vw))', left: '30px' }">
                            ></NewPopupInfo>
                    </span>
                </b-col>

                <b-col cols="12" md="9">
                    <FileUploader :type="'browse'" :ref="'incorporationForm'"
                        :allowed_types="['jpg', 'jpeg', 'png', 'pdf', 'rar', 'zip']" :size="20 * 1024 * 1024"
                        @capture-file="captureFile($event, 'incorporationForm')" @remove-image="removeImage($event)">
                    </FileUploader>
                    <div v-if="error['incorporationForm']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['incorporationForm']) }}
                    </div>
                </b-col>
            </b-row>

            <!-- <b-row class="mb-4">
                <b-col cols="12" md="3">
                    <label>{{ $t('Form of Incorporation') }} <span class="text-danger">*</span></label>
                    <span class="position-relative">
                        <button type="button" class="flat-button position-absolute" @click="togglePopup('showPopup_3')">
                        <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                        </button>
                        <NewPopupInfo :name="'form_incorporation'" v-if="showPopup_3"></NewPopupInfo>
                    </span>
                </b-col>

                <b-col cols="12" md="9">
                    <FileUploader  
                        :type="'browse'"
                        :ref="'form_incorporation'"
                        @capture-file="captureFile($event)" 
                        @remove-image="removeImage($event)">
                    </FileUploader>
                </b-col>
            </b-row> -->

            <b-row class="mb-4">
                <b-col cols="12" md="3">
                    <label>{{ $t('Business Registration Certificate') }} <span class="text-danger">*</span></label>
                    <span class="position-relative">
                        <button type="button" class="flat-button position-absolute" @click="togglePopup('showPopup')">
                            <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                        </button>
                        <NewPopupInfo :name="'business_certificate'" v-if="showPopup" :styleCustom="{ transform: isMobile ? 'translateX(-90%)' : 'translateX(calc(-10vw))', left: '30px' }"></NewPopupInfo>
                    </span>
                </b-col>

                <b-col cols="12" md="9">
                    <FileUploader :type="'browse'" :ref="'businessRegister'"
                        :allowed_types="['jpg', 'jpeg', 'png', 'pdf']" :size="10 * 1024 * 1024"
                        @capture-file="captureFile($event, 'businessRegister')" @remove-image="removeImage($event)">
                    </FileUploader>
                    <div v-if="error['businessRegister']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['businessRegister']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Validity Period of The Business Registration Certificate') }} <span
                            class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <date-picker v-model="params.businessRegisterExpiryDate" :lang="lang_date"
                        input-class="form-control" format="YYYY-MM-DD" value-type="YYYY-MM-DD"
                        @change="onChange('businessRegisterExpiryDate')"></date-picker>
                    <div v-if="error['businessRegisterExpiryDate']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['businessRegisterExpiryDate']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Business Registration Number') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.businessRegisterNo"
                        @keydown="onChange('businessRegisterNo')" />
                    <div v-if="error['businessRegisterNo']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['businessRegisterNo']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-4">
                <b-col cols="12" md="3">
                    <label>{{ $t('Shareholding Structure') }} <span class="text-danger">*</span></label>
                    <span class="position-relative">
                        <button type="button" class="flat-button position-absolute" @click="togglePopup('showPopup_5')">
                            <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                        </button>
                        <NewPopupInfo :name="'equity_structure'" v-if="showPopup_5" :styleCustom="{ transform: isMobile ? 'translateX(-80%)' : 'translateX(calc(-10vw))', left: '30px' }"></NewPopupInfo>
                    </span>
                </b-col>

                <b-col cols="12" md="9">
                    <FileUploader :type="'browse'" :ref="'stockCertificate'"
                        :allowed_types="['jpg', 'jpeg', 'png', 'pdf', 'docx', 'rar', 'zip']" :size="20 * 1024 * 1024"
                        @capture-file="captureFile($event, 'stockCertificate')" @remove-image="removeImage($event)">
                    </FileUploader>
                    <div v-if="error['stockCertificate']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['stockCertificate']) }}
                    </div>
                    <a :href="`${publicPath}img/gep/business/hongkong/Equity structure template.docx`"
                        class="d-block fz-12 text-blue mt-2 text-right">{{ $t('Download shareholding structure template') }}</a>
                </b-col>
            </b-row>

        </div>
    </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import Select2 from 'v-select2-component';

import NewDocOption from "@/components/Kyc/NewDocOption";
import FileUploader from '@/components/Kyc/FileUploader.vue';
import NewPopupInfo from '@/components/NewPopupInfo.vue';
import CreateAccount from '@/services/CreateAccount';

import 'vue2-datepicker/locale/zh-cn';
import 'vue2-datepicker/locale/zh-tw';
import 'vue2-datepicker/locale/ja';
import 'vue2-datepicker/locale/en';
import { transform } from 'd3';

export default {
    data() {
        return {
            publicPath: process.env.BASE_URL != '/' ? process.env.BASE_URL : '',
            cityOpt: [],
            districtOpt: [],
            provinceOpt: [],
            city: '',
            province: '',
            area: '',
            address: '',
            maxSize: 4,
            showPopup: false,
            showPopup_2: false,
            showPopup_3: false,
            showPopup_4: false,
            showPopup_5: false,
            showPopup_6: false,
            lang_date: 'en',
            success: null,
            userNo: null,
            accountId: null,
            optional: ['area'],
            file: {
                orgRegister: null,
                incorporationForm: null,
                declarationForm: null,
                businessRegister: null,
                stockCertificate: null
            },
            params: {
                name: '',
                enName: '',
                province: '',
                city: '',
                area: '',
                address: '',
                orgRegister: '',
                orgRegisterNo: '',
                declarationForm: '',
                incorporationForm: '',
                businessRegister: '',
                businessRegisterExpiryDate: '',
                businessRegisterNo: '',
                stockCertificate: ''
            },
            error: {
                name: '',
                enName: '',
                province: '',
                city: '',
                area: '',
                address: '',
                orgRegister: '',
                orgRegisterNo: '',
                declarationForm: '',
                incorporationForm: '',
                businessRegister: '',
                businessRegisterExpiryDate: '',
                businessRegisterNo: '',
                stockCertificate: ''
            },
            isMobile: window.innerWidth <= 576,
        }
    },
    components: {
        NewDocOption,
        FileUploader,
        Select2,
        NewPopupInfo,
        DatePicker
    },
    props: ['information', 'user_no', 'account_id'],
    methods: {
        getFile(index, file_id) {
            let self = this;
            CreateAccount.getFile({ fileId: file_id }).then(response => {
                let data_file = response.data.data[0];
                if (data_file.type == 8) {
                    self.params.orgRegister = data_file.file;
                    self.$refs['orgRegister'].image = data_file.file;
                }
                else if (data_file.type == 9) {
                    self.params.declarationForm = data_file.file;
                    self.$refs['declarationForm'].image = data_file.file;
                }
                else if (data_file.type == 27) {
                    self.params.incorporationForm = data_file.file;
                    self.$refs['incorporationForm'].image = data_file.file;
                }
                else if (data_file.type == 10) {
                    self.params.businessRegister = data_file.file;
                    self.$refs['businessRegister'].image = data_file.file;
                }
                else if (data_file.type == 11) {
                    self.params.stockCertificate = data_file.file;
                    self.$refs['stockCertificate'].image = data_file.file;
                }
            });
        },
        addressCondition($event) {
            if ($event.keyCode == 13) {
                $event.preventDefault();
            }
            this.onChange('address');
        },
        onchangeAddress($event) {
            let msg = $event.target.value.replace("\n", " ");
            $event.target.value = msg;
        },
        validate() {
            let success = true;
            let self = this;
            Object.keys(this.params).forEach(function (key) {
                if (!self.params[key] && !self.optional.includes(key)) {
                    console.log(key + " can not be blank.");
                    self.error[key] = "can not be blank.";
                    success = false;
                }
            });

            return success;
        },
        uploadFile(account_id, user_no) {
            let self = this;
            this.success = null;
            this.accountId = account_id;
            this.userNo = user_no;

            this.error.orgRegister = '';
            this.error.incorporationForm = '';
            this.error.declarationForm = '';
            this.error.businessRegister = '';
            this.error.stockCertificate = '';

            let file = [];
            if (self.params.orgRegister.indexOf("base64") != -1) {
                file.push({
                    "type": 8,
                    "maxSize": 10 * 1024 * 1024,
                    "file": self.params.orgRegister
                });
            }
            if (self.params.declarationForm.indexOf("base64") != -1) {
                file.push({
                    "type": 9,
                    "maxSize": 20 * 1024 * 1024,
                    "file": self.params.declarationForm
                });
            }
            if (self.params.incorporationForm.indexOf("base64") != -1) {
                file.push({
                    "type": 27,
                    "maxSize": 20 * 1024 * 1024,
                    "file": self.params.incorporationForm
                });
            }
            if (self.params.businessRegister.indexOf("base64") != -1) {
                file.push({
                    "type": 10,
                    "maxSize": 10 * 1024 * 1024,
                    "file": self.params.businessRegister
                });
            }
            if (self.params.stockCertificate.indexOf("base64") != -1) {
                file.push({
                    "type": 11,
                    "maxSize": 20 * 1024 * 1024,
                    "file": self.params.stockCertificate
                });
            }


            if (file.length > 0) {
                CreateAccount.uploadFile({
                    "accountId": self.accountId,
                    "userNo": "" + self.userNo,
                    "fileUpload": file
                }).then(response => {
                    const data = response.data.data;
                    let fileList = data["file"];
                    for (let i = 0; i < fileList.length; i++) {
                        if (fileList[i].type == 8) {
                            // self.params.orgRegister = fileList[i].file;
                            self.file.orgRegister = fileList[i].fileId;
                        }
                        else if (fileList[i].type == 9) {
                            // self.params.declarationForm = fileList[i].file;
                            self.file.declarationForm = fileList[i].fileId;
                        }
                        else if (fileList[i].type == 27) {
                            // self.params.declarationForm = fileList[i].file;
                            self.file.incorporationForm = fileList[i].fileId;
                        }
                        else if (fileList[i].type == 10) {
                            // self.params.businessRegister = fileList[i].file;
                            self.file.businessRegister = fileList[i].fileId;
                        }
                        else if (fileList[i].type == 11) {
                            // self.params.stockCertificate = fileList[i].file;
                            self.file.stockCertificate = fileList[i].fileId;
                        }
                    }
                    self.success = true;
                    self.$emit('file-uploaded');
                }).catch(err => {
                    console.log("===== err ======");
                    console.log(err);
                    self.checkError(err);
                });
            }
            else {
                self.success = true;
                self.$emit('file-uploaded');
            }
        },
        checkError(err) {
            try {
                let self = this;
                const response = err.response;
                console.log("====res=====");
                console.log(response);
                if (response.status == 400) {
                    if (typeof response.data.message === 'string') {
                        this.$notify({
                            type: 'warn',
                            text: response.data.message
                        });
                    }
                    else {
                        console.log(response.data);
                        if (response.data.message['file']) {
                            let fileList = response.data.message['file'];
                            for (let i = 0; i < fileList.length; i++) {
                                if (fileList[i].type == 8) {
                                    console.log("masuk");
                                    this.error.orgRegister = fileList[i].message;
                                }
                                else if (fileList[i].type == 9) {
                                    this.error.declarationForm = fileList[i].message;
                                }
                                else if (fileList[i].type == 27) {
                                    this.error.incorporationForm = fileList[i].message;
                                }
                                else if (fileList[i].type == 10) {
                                    this.error.businessRegister = fileList[i].message;
                                }
                                else if (fileList[i].type == 11) {
                                    this.error.stockCertificate = fileList[i].message;
                                }
                            }
                        }
                        self.success = false;
                        self.$emit('on-error');
                    }
                }
                else if (response.status == 401 && response.data.name == "Unauthorized") {
                    localStorage.removeItem("token");
                    localStorage.removeItem("member");
                    localStorage.removeItem("isFirstTime");
                    this.$router.push({
                        path: '/login',
                    });
                }
                else {
                    if (localStorage.getItem("token")) {
                        var msg = (response.data) ? response.data.message : err.message;
                        this.$notify({
                            type: 'warn',
                            text: msg
                        })
                    }
                }
            } catch (e) {
                console.log(err);
            }
        },
        onSelectDistrict({ id, text }) {
            var self = this;
            this.onChange('area');
        },
        onSelectCity({ id, text, code }) {
            var self = this;
            this.onChange('city');
            if (text) {
                this.area = "";
            }
            this.getDistrict(code);
        },
        onSelectProvince({ id, text, code }) {
            var self = this;
            this.onChange('province');
            self.districtOpt = [];
            if (text) {
                this.city = "";
                this.area = "";
            }
            this.getCity(code);
        },
        getDistrict(code) {
            this.districtOpt = [];
            CreateAccount.getDistrict({ "city_id": code }).then(response => {
                const data = response.data.data;
                data.forEach(item => {
                    this.districtOpt.push({ "id": item.value, "text": item.text, "code": item.code });
                });
            }).catch(err => {
                this.checkError(err);
            });
        },
        getCity(code) {
            this.cityOpt = [];
            CreateAccount.getCity({ "province_id": code }).then(response => {
                const data = response.data.data;
                data.forEach(item => {
                    this.cityOpt.push({ "id": item.value, "text": item.text, "code": item.code });
                });
            }).catch(err => {
                this.checkError(err);
            });
        },
        onChange(name) {
            this.error[name] = "";
        },
        togglePopup(item) {
            this[item] = !this[item];
        },
        selectedDocumentBeneficiary(data) {
            this.doc_type = data.index;
        },
        captureFile($event, key) {
            this.error[key] = "";
            this.params[key] = $event;
        },
        removeImage($event) {
            console.log($event);
        },
        doEscapeMarkup(markup) {
            return markup;
        },
        updateIsMobile() {
            this.isMobile = window.innerWidth <= 576;
        },
    },
    mounted() {
        let self = this;
        this.$nextTick(() => {

            CreateAccount.getProvince({ "country": "HKG" }).then(response => {
                const data = response.data.data;
                this.provinceOpt = [];
                data.forEach(item => {
                    this.provinceOpt.push({ "id": item.value, "text": item.text, "code": item.id });
                });

                if (this.information) {

                    Object.keys(this.params).forEach(function (key) {
                        if (typeof self.file[key] !== "undefined" && self.information[key]) {
                            self.file[key] = self.information[key];
                            self.getFile(key, self.information[key]);
                        }
                        else {
                            self.params[key] = self.information[key];
                        }
                    });

                    if (this.params.province) {
                        let data_province = this.params.province.split("_");
                        this.getCity(data_province[0]);
                    }

                    if (this.params.city) {
                        let data_city = this.params.city.split("_");
                        this.getDistrict(data_city[0]);
                    }
                }

                if (this.user_no) {
                    this.userNo = this.user_no;
                    this.accountId = this.account_id;
                }

            }).catch(err => {
                this.checkError(err);
            });

            window.addEventListener('resize', this.updateIsMobile);
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateIsMobile);
    },
}
</script>